import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as api from '../api/scheduleApi';
import type { ActiveScenarioDto } from '../types';

export const scheduleKeys = {
  all: ['schedule'] as const,
  day: (locationId: string, date: string) => [...scheduleKeys.all, 'day', locationId, date] as const,
  active: (locationId: string, date: string) => [...scheduleKeys.all, 'active', locationId, date] as const,
  report: (locationId: string, date: string) => [...scheduleKeys.all, 'report', locationId, date] as const,
};

export function useDaySchedule(locationId: string | undefined, date: string) {
  return useQuery({
    queryKey: scheduleKeys.day(locationId ?? '', date),
    queryFn: () => api.getDaySchedule(locationId!, date),
    enabled: !!locationId,
  });
}

export function useGenerateSchedule(locationId: string, date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.generateSchedule(locationId, date),
    onSuccess: (data) => qc.setQueryData(scheduleKeys.day(locationId, date), data),
  });
}

export function useSetExclusion(locationId: string, date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { scheduleItemId: string; excluded: boolean }) =>
      api.setScheduleItemExclusion(locationId, vars.scheduleItemId, date, vars.excluded),
    onSuccess: () => qc.invalidateQueries({ queryKey: scheduleKeys.day(locationId, date) }),
  });
}

export function useActiveScenarios(locationId: string | undefined, date: string) {
  return useQuery({
    queryKey: scheduleKeys.active(locationId ?? '', date),
    queryFn: () => api.getActiveScenarios(locationId!, date),
    enabled: !!locationId,
  });
}

export function useReorderScenarios(locationId: string, date: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scenarioIds: string[]) => api.reorderScenarios(locationId, scenarioIds),
    onMutate: async (scenarioIds) => {
      await qc.cancelQueries({ queryKey: scheduleKeys.active(locationId, date) });
      const previous = qc.getQueryData<ActiveScenarioDto[]>(scheduleKeys.active(locationId, date));
      if (previous) {
        const byId = new Map(previous.map((s) => [s.scenarioId, s]));
        const reordered = scenarioIds
          .map((id, i) => {
            const s = byId.get(id);
            return s ? { ...s, priority: i, isWinner: i === 0 } : undefined;
          })
          .filter((s): s is ActiveScenarioDto => !!s);
        qc.setQueryData(scheduleKeys.active(locationId, date), reordered);
      }
      return { previous };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.previous) qc.setQueryData(scheduleKeys.active(locationId, date), ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: scheduleKeys.active(locationId, date) }),
  });
}

export function usePlaybackReport(locationId: string | undefined, date: string, enabled: boolean) {
  return useQuery({
    queryKey: scheduleKeys.report(locationId ?? '', date),
    queryFn: () => api.getPlaybackReport(locationId!, date),
    enabled: !!locationId && enabled,
  });
}
